<script setup lang="ts">
/**
 *
 * @name VentanaMantenimiento
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE QUE MUESTRA UNA VENTANA DE MANTENIMIENTO
 *
 * responsabilidades:
 * R1: PERMITE VISUALIZAR LA VENTANA DE MANTENIMIENTO
 * R2: PERMITE ASIGNAR INSTITUCIONES AL USUARIO ACTUAL
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { IMantenimiento } from '@shared/types/base'
import { ETipoMantenimientoType } from '@shared/types/enums'
import { hasAdminRole, listenMantenimiento } from '@/components/utils/functions'
import { formatDate } from '@/utils/dateFormatter'
import { useStore } from 'vuex'
const showModal = ref(false)
const showAlert = ref(false)
const mantenimiento = ref<IMantenimiento | null>(null)
const store = useStore()
const modalData = computed<IMantenimiento>(() => mantenimiento.value ?? { mensaje: '', titulo: '', tipo: ETipoMantenimientoType.INFO })

const modalMessage = computed(() => modalData.value.mensaje?.trim() || 'No hay información disponible.')
const isAdmin = ref(false)
const isHardLock = computed(() => modalData.value.tipo === ETipoMantenimientoType.MAINTENANCE)
const hasDateRange = computed(() => Boolean(modalData.value.fechaInicio && modalData.value.fechaFin))
const fechaInicioFormatted = computed(() => formatDate(modalData.value.fechaInicio, 'es-MX-HH:mm'))
const fechaFinFormatted = computed(() => formatDate(modalData.value.fechaFin, 'es-MX-HH:mm'))

const statusIcon = computed(() => {
	switch (modalData.value.tipo) {
		case ETipoMantenimientoType.MAINTENANCE:
			return 'event'
		case ETipoMantenimientoType.WARNING:
			return 'warning'
		case ETipoMantenimientoType.ERROR:
			return 'error'
		case ETipoMantenimientoType.INFO:
		default:
			return 'info'
	}
})

const statusClass = computed(() => {
	switch (modalData.value.tipo) {
		case ETipoMantenimientoType.ERROR:
			return 'status-error'
		case ETipoMantenimientoType.WARNING:
			return 'status-warning'
		case ETipoMantenimientoType.MAINTENANCE:
			return 'status-maintenance'
		case ETipoMantenimientoType.INFO:
		default:
			return 'status-info'
	}
})

const headerClass = computed(() => {
	switch (modalData.value.tipo) {
		case ETipoMantenimientoType.ERROR:
			return 'bg-gradient-error'
		case ETipoMantenimientoType.WARNING:
			return 'bg-gradient-warning'
		case ETipoMantenimientoType.MAINTENANCE:
			return 'bg-gradient-maintenance'
		case ETipoMantenimientoType.INFO:
		default:
			return 'bg-gradient-info'
	}
})

const closeModal = () => {
	showModal.value = false
}

const closeAlert = () => {
	showAlert.value = false
}

const applyMantenimiento = (data: IMantenimiento | null) => {
	mantenimiento.value = data

	const hasContent = Boolean(data?.mensaje?.trim() || data?.titulo?.trim())

	// Si hay contenido, limpiar el flag de recarga para permitir recargas futuras
	if (hasContent) {
		const reloadKey = 'mantenimiento-reload-attempted'
		sessionStorage.removeItem(reloadKey)
	}

	if (!hasContent) {
		showModal.value = false
		showAlert.value = false

		// Recargar solo una vez para evitar bucle infinito
		const reloadKey = 'mantenimiento-reload-attempted'
		const hasReloaded = sessionStorage.getItem(reloadKey)

		if (!hasReloaded) {
			sessionStorage.setItem(reloadKey, 'true')
			nextTick(() => {
				globalThis.location.reload()
			})
		}
		return
	}

	if (data?.tipo === ETipoMantenimientoType.MAINTENANCE) {
		showModal.value = true
		showAlert.value = false
		return
	}

	showAlert.value = true
	showModal.value = false
}
let stopMantenimientoListener: null | (() => void) = null

onMounted(async () => {
	isAdmin.value = await hasAdminRole(store.state.user.uid)
	stopMantenimientoListener = listenMantenimiento(applyMantenimiento)
})

onUnmounted(() => {
	stopMantenimientoListener?.()
	stopMantenimientoListener = null
})
</script>

<template>
	<div>
		<VaModal v-model="showModal" blur hide-default-actions :no-esc-dismiss="isHardLock" :no-outside-dismiss="isHardLock" :no-dismiss="isHardLock" max-height="520px" max-width="720px">
			<VaCard class="mantenimiento-card">
				<div class="mantenimiento-header" :class="headerClass">
					<div class="mantenimiento-header-left">
						<div class="status-badge" :class="statusClass">
							<VaIcon class="status-icon" :name="statusIcon" />
							<span>{{ modalData.titulo }}</span>
						</div>
					</div>
				</div>

				<VaCardContent class="mantenimiento-body">
					<div v-if="isHardLock" class="mantenimiento-hardlock">
						<VaIcon name="warning" class="hardlock-icon" />
						<p class="hardlock-text">{{ modalMessage }}</p>
					</div>
					<p v-else class="mantenimiento-message">
						{{ modalMessage }}
					</p>

					<div v-if="hasDateRange" class="mantenimiento-range">
						<div class="mantenimiento-range-item">
							<VaIcon name="event" class="range-icon" />
							<span class="range-label">Inicio:</span>
							<span class="range-value">{{ fechaInicioFormatted }}</span>
						</div>
						<div class="mantenimiento-range-item">
							<VaIcon name="event" class="range-icon" />
							<span class="range-label">Fin:</span>
							<span class="range-value">{{ fechaFinFormatted }}</span>
						</div>
					</div>
				</VaCardContent>
				<div v-if="!isHardLock || isAdmin" class="footer-actions">
					<div class="flex justify-end">
						<VaButton class="action-button" color="primary" @click="closeModal">
							<VaIcon name="close" class="mr-2" />
							Cerrar
						</VaButton>
					</div>
				</div>
			</VaCard>
		</VaModal>
		<VaModal v-model="showAlert" blur hide-default-actions max-height="520px" max-width="720px">
			<VaCard class="mantenimiento-card">
				<div class="mantenimiento-header" :class="headerClass">
					<div class="mantenimiento-header-left">
						<div class="status-badge" :class="statusClass">
							<VaIcon class="status-icon" :name="statusIcon" />
							<span>{{ modalData.titulo }}</span>
						</div>
					</div>
				</div>

				<VaCardContent class="mantenimiento-body">
					<div v-if="isHardLock" class="mantenimiento-hardlock">
						<VaIcon name="warning" class="hardlock-icon" />
						<p class="hardlock-text">{{ modalData.mensaje }}</p>
					</div>
					<p v-else class="mantenimiento-message">
						{{ modalData.mensaje }}
					</p>

					<div v-if="hasDateRange" class="mantenimiento-range">
						<div class="mantenimiento-range-item">
							<VaIcon name="event" class="range-icon" />
							<span class="range-label">Inicio:</span>
							<span class="range-value">{{ fechaInicioFormatted }}</span>
						</div>
						<div class="mantenimiento-range-item">
							<VaIcon name="event" class="range-icon" />
							<span class="range-label">Fin:</span>
							<span class="range-value">{{ fechaFinFormatted }}</span>
						</div>
					</div>
				</VaCardContent>
				<div v-if="!isHardLock || isAdmin" class="footer-actions">
					<div class="flex justify-end">
						<VaButton class="action-button" color="primary" @click="closeAlert">
							<VaIcon name="close" class="mr-2" />
							Cerrar
						</VaButton>
					</div>
				</div>
			</VaCard>
		</VaModal>
	</div>
</template>

<style scoped>
.mantenimiento-card {
	border-radius: 12px;
	overflow: hidden;
}

.mantenimiento-header {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
}
.mantenimiento-header-left {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.mantenimiento-title {
	font-size: 1.5rem;
	font-weight: 700;
}

.mantenimiento-body {
	padding: 1.25rem;
}

.mantenimiento-message {
	margin: 0;
	color: #1a2a3a;
	font-size: 1.05rem;
	line-height: 1.6;
	white-space: pre-line;
}

.mantenimiento-range {
	margin-top: 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.75rem 1rem;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	background: #f8f9fa;
}

.mantenimiento-range-item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #1a2a3a;
	font-size: 0.9rem;
}
.range-icon {
	color: #007bff;
}
.range-label {
	font-weight: 600;
	color: #6c757d;
}
.range-value {
	font-weight: 600;
}

.mantenimiento-hardlock {
	margin-top: 1rem;
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	padding: 0.75rem 1rem;
	border-radius: 12px;
	border: 1px solid #fcd34d;
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
}
.hardlock-icon {
	margin-top: 2px;
}
.hardlock-text {
	font-size: 0.95rem;
	line-height: 1.4;
	font-weight: 600;
}

.status-badge {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border-radius: 12px;
	font-size: 0.875rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	width: fit-content;
}
.status-info {
	background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
	color: #075985;
	border: 1px solid #7dd3fc;
}
.status-warning {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}
.status-error {
	background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
	color: #991b1b;
	border: 1px solid #f87171;
}
.status-maintenance {
	background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
	color: #676567;
	border: 1px solid #777578;
}
.status-icon {
	font-size: 1rem;
}

.bg-gradient-info {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}
.bg-gradient-warning {
	background: linear-gradient(90deg, #f59e0b 0%, #b45309 100%);
}
.bg-gradient-error {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
.bg-gradient-maintenance {
	background: linear-gradient(90deg, #bab8be 0%, #777578 100%);
}

@media (max-width: 768px) {
	.mantenimiento-header {
		padding: 0.9rem 1rem;
	}
	.mantenimiento-body {
		padding: 1rem;
	}
	.mantenimiento-title {
		font-size: 1.25rem;
	}
	.mantenimiento-message {
		font-size: 1rem;
	}
}
</style>
